import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * SidebarBillingCard — premium dark widget at the bottom of the sidebar.
 *
 * Shows at-a-glance plan status + credits remaining, with a CTA to the
 * /app/financeiro page. Replaces the older workspace/tour/logout buttons
 * that used to live in the same slot.
 *
 * Auto-refreshes every 60s while mounted so a campaign blast doesn't
 * leave a stale counter for hours.
 */
export default function SidebarBillingCard() {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/credits/wallet");
        if (alive) setW(data);
      } catch {
        if (alive) setW(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const balance = w?.balance ?? 0;
  const monthlyGrant = w?.monthly_grant ?? 0;
  const monthlyBalance = w?.monthly_balance ?? 0;
  const bonusBalance = w?.bonus_balance ?? 0;
  const packBalance = w?.pack_balance ?? 0;
  const periodTotal = w?.period_total ?? 0;
  const enforcement = !!w?.enforcement_enabled;
  // Has the operator received any bonus credits (admin gift / promo)
  // or purchased packs? If yes, the "franquia 15k" label by itself is
  // misleading — they have MORE pool available this cycle. Show the
  // real number plus a "+ bônus" hint instead.
  const hasExtras = (bonusBalance + packBalance) > 0;
  // While shadow-mode is on, the "plan" is effectively unlimited — so we
  // celebrate that rather than scaring the operator with a low number.
  const isLow =
    enforcement && monthlyGrant > 0 && balance / monthlyGrant < 0.2;

  return (
    <Link
      to="/app/financeiro"
      data-testid="sidebar-billing-card"
      className="group relative block rounded-xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border border-slate-700/60 shadow-lg shadow-slate-900/20 hover:border-slate-600 transition-colors"
    >
      {/* Subtle radial glow on hover */}
      <div className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full bg-white/0 group-hover:bg-white/10 blur-2xl transition-colors duration-500" />

      <div className="relative p-3.5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${isLow ? "bg-rose-400" : "bg-white"} shadow-[0_0_8px] ${isLow ? "shadow-rose-400/70" : "shadow-white/40"}`} />
            <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium" data-testid="sidebar-billing-status">
              {enforcement ? "Plano ativo" : "Plano ativo"}
            </span>
          </div>
          <Sparkle size={11} weight="fill" className="text-white/60" />
        </div>

        <div className="flex items-baseline gap-1.5">
          <span className="text-[24px] leading-none font-heading font-semibold text-white tabular-nums" data-testid="sidebar-billing-balance">
            {loading ? "—" : balance.toLocaleString("pt-BR")}
          </span>
          <span className="text-[11px] text-slate-400 font-medium">créditos</span>
        </div>

        <div className="mt-1 flex items-center gap-1 text-[10.5px] text-slate-500" data-testid="sidebar-billing-grant">
          <Lightning size={9} weight="fill" className="text-slate-500" />
          {monthlyGrant > 0 ? (
            hasExtras ? (
              <span
                title={`Mês: ${monthlyBalance.toLocaleString("pt-BR")} (expira na renovação)\nBônus: ${bonusBalance.toLocaleString("pt-BR")}\nPacotes: ${packBalance.toLocaleString("pt-BR")}\nFranquia base do plano: ${monthlyGrant.toLocaleString("pt-BR")}/mês`}
              >
                pool {(periodTotal || balance).toLocaleString("pt-BR")} este mês{" "}
                <span className="text-emerald-300">· + bônus</span>
              </span>
            ) : (
              <>franquia {monthlyGrant.toLocaleString("pt-BR")}/mês</>
            )
          ) : (
            <>uso ilimitado em observação</>
          )}
        </div>

        <div className="mt-3 pt-2.5 border-t border-slate-700/50 flex items-center justify-between" data-testid="sidebar-billing-cta">
          <span className="text-[11px] text-slate-300 font-medium tracking-tight">
            Gerenciar plano
          </span>
          <ArrowRight size={11} weight="bold" className="text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
}
